/**
 * GoldFire Network (https://goldfire.me)
 * Copyright (c) 2012 - 2020, GoldFire Studios, Inc.
 * http://goldfirestudios.com
 */

import {store} from './client-create-app';
import Primus from './assets/js/primus-client';
import * as events from './serverEvents';
import {readCookie} from './utils';

let io = {};
const requests = {};
let subscriptions = [];

/**
 * Send a request to the server and then expect a response back.
 * @param  {String} route  Namespace of the call.
 * @param  {String} action Method name on server.
 * @param  {Object}   data Payload to send.
 * @param  {Function} cb   Callback when the server responds.
 */
export const request = (route, action, data, cb = () => {}) => {
  const reqId = Math.random().toString(36).substr(2, 7);
  const tab = store.state.toolbar.activeTab;
  requests[reqId] = {
    reqId,
    cb,
    context: {
      tab,
      view: store.state[tab] ? store.state[tab].activeView : '',
    },
  };

  io.write({route, action, reqId, data, sid: readCookie('sid')});
};

/**
 * Cancel the requests associated with a tab/view context.
 * @param  {String} tab  Tab context.
 * @param  {String} view View context.
 */
export const cancelRequests = (tab, view) => {
  Object.keys(requests).forEach((key) => {
    if (requests[key].context.tab === tab && requests[key].context.view === view) {
      delete requests[key];
    }
  });
};

/**
 * Send a message to the server without expecting to hear back.
 * @param  {String} route  Namespace of the call.
 * @param  {String} action Method name on server.
 * @param  {Object} data   Payload to send.
 */
export const send = (route, action, data = {}) => {
  io.write({route, action, data, sid: readCookie('sid')});
};

/**
 * Subscribe to a channel to receive live updates from the server.
 *
 * Channel format: ACTION:ID
 *   Action: The client-side method to call when receiving published data.
 *   Id: Not always necessary, but could reference a specific chat thread.
 *
 * @param  {String} channel Channel name.
 */
export const subscribe = (channel) => {
  send('pubsub', 'subscribe', {channel});

  // Store the subscription for reconnects.
  if (!subscriptions.includes(channel)) {
    subscriptions.push(channel);
  }
};

/**
 * Unsubscribe from a channel.
 *
 * Channel format: ACTION:ID
 *   Action: The client-side method to call when receiving published data.
 *   Id: Not always necessary, but could reference a specific chat thread.
 *
 * @param  {String} channel Channel name.
 */
export const unsubscribe = (channel) => {
  send('pubsub', 'unsubscribe', {channel});

  // Remove this channel from the stored list.
  const index = subscriptions.indexOf(channel);
  if (index >= 0) {
    subscriptions.splice(index, 1);
  }
};

/**
 * Subscribe to multiple channels to receive live updates from the server.
 * @param  {Array} channels Channels to subscribe to.
 */
export const bulkSubscribe = (channels) => {
  if (!channels.length) {
    return;
  }

  send('pubsub', 'bulkSubscribe', {channels});

  // Store the subscription for reconnects.
  channels.forEach((channel) => {
    if (!subscriptions.includes(channel)) {
      subscriptions.push(channel);
    }
  });
};

/**
 * Unsubscribe from multiple channels.
 * @param  {Array} channels Channels to unsubscribe from.
 */
export const bulkUnsubscribe = (channels) => {
  if (!channels.length) {
    return;
  }

  send('pubsub', 'bulkUnsubscribe', {channels});

  // Remove these channels from the stored list.
  channels.forEach((channel) => {
    const index = subscriptions.indexOf(channel);
    if (index >= 0) {
      subscriptions.splice(index, 1);
    }
  });
};

// Setup the connection to the WebSocket server.
if (typeof window !== 'undefined') {
  let wsUrl = typeof Dev !== 'undefined' ? Dev.ws : 'ws://localhost:8000';
  if (window.location.host === 'beta.goldfire.me') {
    wsUrl = 'wss://beta.goldfire.me';
  } else if (window.location.host === 'goldfire.me') {
    wsUrl = 'wss://goldfire.me';
  }

  io = new Primus(`${wsUrl}/primus`, {
    reconnect: {
      min: 250,
      retries: 50,
    },
    pingTimeout: 45000,
  });

  // Receive responses from the server and correctly handle the data.
  io.on('data', (msg) => {
    // Route this message to the appropriate request callback.
    // If it isn't a request/response, handle the appropriate action.
    if (msg.reqId) {
      if (requests[msg.reqId]) {
        requests[msg.reqId].cb(msg.data);
        delete requests[msg.reqId];
      }
    } else if (msg.action && typeof events[msg.action] === 'function') {
      const action = events[msg.action];
      delete msg.action;
      action(msg);
    } else if (msg.mutation) {
      store.commit(msg.mutation, msg.data);
    }
  });

  // Listen for disconnection events to track how long we were disconnected for.
  let disconnected = null;
  io.on('close', () => {
    console.log('DISCONNECTED [GoldFire]');
    disconnected = Date.now();
  });

  // Listen for new connection event to handle re-renders.
  io.on('open', () => {
    console.log('CONNECTED [GoldFire]');
    // If it has been more than 10 seconds, re-render the current view to get fresh data.
    if (typeof disconnected === 'number' && Date.now() - 10000 > disconnected) {
      const tab = store.state.toolbar.activeTab;

      if (tab && tab !== 'empty') {
        const view = store.state[tab].activeView;
        const history = store.state.panel.history.slice(0);

        store.commit('changeView', 'empty-view');
        setTimeout(() => {
          store.commit('changeView', view);
          store.commit('setHistory', history);
        }, 1);
      }
    }

    // Re-subscribe to all subscriptions.
    bulkSubscribe(subscriptions);

    // Update the active game this player is on.
    if (typeof disconnected === 'number' && store.state.activeGame && store.state.activeGame.gid) {
      store.dispatch('updateActiveGame', {gid: store.state.activeGame.gid});
    }

    // Re-sync the toolbar counters.
    if (typeof disconnected === 'number') {
      store.dispatch('loadLauncher');
    }
  });
}
